import { executeV2Flow, executeV2FlowStream } from '../api/studioV2.api';
import { executeFlow, executeFlowStreaming, getFlow } from '../api/agenticFlow.api';

/** True when app was loaded from app/v2 (Mongo flow refs, not legacy AgenticFlow). */
export function usesV2FlowRuntime(config) {
  return config?.runtimeSource === 'v2app';
}

/** Normalize V2 SSE payloads to the shape DashboardContext expects. */
export function normalizeFlowStreamUpdate(update) {
  if (!update || typeof update !== 'object') return update;
  const data = update.data && typeof update.data === 'object' ? update.data : {};
  return {
    ...update,
    ...data,
    type: update.type ?? data.type,
    execution_id: update.execution_id ?? update.executionId ?? data.execution_id ?? data.executionId,
    waiting_for_form_input: update.waiting_for_form_input ?? data.waiting_for_form_input,
    form_fields: update.form_fields ?? data.form_fields,
    form_node_waiting: update.form_node_waiting ?? data.form_node_waiting,
    form_title: update.form_title ?? data.form_title,
    form_description: update.form_description ?? data.form_description,
    ai_response: update.ai_response ?? data.ai_response,
    node_id: update.node_id ?? data.node_id,
    data: Object.keys(data).length ? data : update,
  };
}

/**
 * Start streaming flow execution (V2 API for v2 apps, legacy AgenticFlow otherwise).
 * `onStreamEnd` is called once a V2 stream has been read to the end; a paused run's
 * `approval_required` event arrives after `flow_complete`, just before the end.
 * @returns {{ close: Function, isV2: boolean }}
 */
export async function connectFlowExecution({
  flowId,
  flowInput,
  config,
  onUpdate,
  onStreamError,
  onStreamEnd,
}) {
  if (usesV2FlowRuntime(config)) {
    const controller = new AbortController();
    executeV2FlowStream(
      flowId,
      flowInput,
      (raw) => onUpdate(normalizeFlowStreamUpdate(raw)),
      { signal: controller.signal },
    ).then(() => onStreamEnd?.(), (e) => {
      if (e?.name !== 'AbortError') onStreamError?.(e);
    });
    return { close: () => controller.abort(), isV2: true };
  }

  const { response: rawFlow, err: flowErr } = await getFlow(flowId);
  if (flowErr || !rawFlow) {
    throw new Error(flowErr?.message || 'Failed to fetch flow');
  }
  const flowData = rawFlow?.flow ?? rawFlow;
  const conn = await executeFlowStreaming(
    flowData,
    flowInput,
    onUpdate,
    onStreamError,
  );
  return { close: conn?.close ?? (() => {}), isV2: false, flowData };
}

/** Poll/non-stream fallback after stream failure. */
export async function executeFlowPollFallback({ flowId, flowInput, config }) {
  if (usesV2FlowRuntime(config)) {
    const { result, error } = await executeV2Flow(flowId, flowInput);
    if (error) return { err: new Error(error) };
    const payload = result?.result ?? result?.data ?? result;
    return {
      response: {
        result: payload,
        executionId: result?.execution_id ?? result?.executionId,
        status: result?.status ?? 'completed',
      },
    };
  }
  return executeFlow(flowId, flowInput);
}
